/**
 * @file PlanDocumentNavigator.jsx
 * @module ui/src/components/tenant-admin/PlanDocumentNavigator
 * @description
 *   Renders the controlled left-rail navigator for the eight plan-document sections.
 *   Completion indicators communicate structural integrity only and never governance approval.
 *
 * @version 1.0.0
 * @since S755
 * @author data-fit LLC · DecisionFlow · S776
 *
 * @architecture
 *   Locked decisions : #1, #6
 *   Temporal invariants : none
 *   Design principles : DP-5, DP-14
 *   Cross-cutting contracts : XC-5
 *
 * @domain-rules
 *   - Section completion represents structural document integrity and must never be presented as approval state.
 *   - Credit Allocation visibility is controlled only by the section.visible value supplied by the document layer.
 *   - Governance workflow status never determines whether a document section appears.
 *
 * @session-6-migration-flags
 *   - none
 *
 * @changelog
 *   1.0.0  S755  Initial build — Group E batch delivery
 */

import PropTypes from 'prop-types';

/**
 * @typedef {'complete'|'incomplete'|'optional'|'warning'} CompletionState
 * S812 canonical states. 'blocking' removed. 'optional' added for Adjustments/True-Up.
 * 'warning' = complete with advisory — counts as completeLike for Overview/Review derivation.
 */

/**
 * @typedef {Object.<string, CompletionState>} SectionStateMap
 * S812: sections is a plain object keyed by section key from deriveSectionStates().
 * e.g. { 'identity': 'warning', 'credit-allocation': 'complete', ... }
 */

/**
 * @typedef {Object} PlanDocumentNavigatorProps
 * @property {SectionStateMap} sections — keyed completion state object from usePlanDocument
 * @property {string} activeSection
 * @property {Function} onSectionSelect
 */

// S812 canonical labels — locked D6. No step numbers. No 'assignment-scope' — D3 locked S811.
// Label rationale (SME confirmed): "Plan Basics" not "Identity"; "Crediting" not "Credit Allocation";
// "Performance & Rates" not "Measurement & Economics"; "Adjustments" not "Payout Adjustments";
// "True-Up" not "Settlement"; "Review & Submit" not "Review & Governance".
const LOCKED_SECTION_ORDER = [
  { key: 'overview',              label: 'Overview' },
  { key: 'identity',              label: 'Plan Basics' },
  { key: 'credit-allocation',     label: 'Crediting' },
  { key: 'measurement-economics', label: 'Performance & Rates' },
  { key: 'payout-adjustments',    label: 'Adjustments' },
  { key: 'settlement',            label: 'True-Up' },
  { key: 'review-governance',     label: 'Review & Submit' },
  // assignment-scope REMOVED — D3 locked S811. Assignment is the Assignments tab's responsibility.
];

// S812 canonical completion states. 'blocking' removed — not in S812 spec.
// 'optional' added — grey dash for sections where having nothing configured is valid
// (Adjustments with no modifiers enabled; True-Up with policy=none).
// 'warning' = yellow ! — complete with advisory; counts as completeLike for derivations.
const COMPLETION_PRESENTATION = {
  complete:   { icon: '✓', label: 'Complete',  color: 'var(--color-success, #16a34a)' },
  incomplete: { icon: '○', label: 'Incomplete', color: 'var(--color-text-muted, #6b7280)' },
  optional:   { icon: '–', label: 'Optional',   color: 'var(--color-text-muted, #6b7280)' },
  warning:    { icon: '!', label: 'Warning',    color: 'var(--color-warning, #ca8a04)' },
};

const styles = {
  nav: {
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    width: '260px',
    minWidth: '260px',
    borderRight: '1px solid rgb(var(--color-border))',
    background: 'rgb(var(--color-surface))',
    padding: '12px',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  item: {
    width: '100%',
  },
  button: {
    width: '100%',
    border: 0,
    background: 'transparent',
    color: 'rgb(var(--color-text))',
    font: 'inherit',
    textAlign: 'left',
    padding: '10px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  activeButton: {
    background: 'rgb(var(--color-selected-bg))',
    fontWeight: 600,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  completion: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    color: 'rgb(var(--color-text-muted))',
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
  },
};

/**
 * Controlled plan-document navigation.
 *
 * @param {PlanDocumentNavigatorProps} props
 * @returns {JSX.Element}
 */
export default function PlanDocumentNavigator({
  sections,
  activeSection,
  onSectionSelect,
}) {
  // WHY: sections is a plain object keyed by section key (e.g. { 'identity': 'warning', ... })
  // returned by deriveSectionStates(). The locked LOCKED_SECTION_ORDER controls presentation
  // order — runtime state cannot reorder sections.
  // sections may be an array (legacy) or object (S812). Normalise defensively.
  const sectionStateByKey = Array.isArray(sections)
    ? new Map(sections.map((s) => [s.key, s]))
    : new Map(Object.entries(sections).map(([key, state]) => [key, { key, completionState: state, visible: true }]));

  return (
    <nav
      style={styles.nav}
      aria-label="Plan document sections"
      data-component="PlanDocumentNavigator"
    >
      <ul style={styles.list}>
        {LOCKED_SECTION_ORDER.map((lockedSection) => {
          // WHY: Structural state comes from the document hook while labels and order remain locked by the workspace specification.
          const suppliedSection = sectionStateByKey.get(lockedSection.key) || {
            key: lockedSection.key,
            label: lockedSection.label,
            completionState: 'incomplete',
            visible: true,
          };

          // WHY: Section visibility is respected only from the structural visible flag; no workflow or approval state participates.
          if (suppliedSection.visible === false) {
            return null;
          }

          // WHY: Every supported completion state has both a visual glyph and a textual name so state is never encoded by iconography alone.
          const completion =
            COMPLETION_PRESENTATION[suppliedSection.completionState] ||
            COMPLETION_PRESENTATION.incomplete;

          // WHY: Active navigation styling reflects the controlled route only and does not imply governance state.
          const isActive = activeSection === lockedSection.key;

          // WHY: Section selection is delegated upward so this navigator owns no route or domain state.
          const handleSelect = () => {
            onSectionSelect(lockedSection.key);
          };

          return (
            <li
              key={lockedSection.key}
              style={styles.item}
            >
              <button
                type="button"
                style={{
                  ...styles.button,
                  ...(isActive ? styles.activeButton : {}),
                }}
                aria-current={isActive ? 'page' : undefined}
                onClick={handleSelect}
              >
                <span style={styles.labelRow}>
                  <span>{lockedSection.label}</span>

                  <span
                    style={{ ...styles.completion, color: completion.color }}
                    aria-label={`Section status: ${completion.label}`}
                  >
                    <span aria-hidden="true">{completion.icon}</span>
                    <span>{completion.label}</span>
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

PlanDocumentNavigator.propTypes = {
  // S812: sections is a plain object keyed by section key, values are completion state strings.
  // e.g. { 'identity': 'warning', 'credit-allocation': 'complete', ... }
  // Accepts array (legacy shape) defensively — normalised in render.
  sections: PropTypes.oneOfType([
    PropTypes.objectOf(
      PropTypes.oneOf(['complete', 'incomplete', 'optional', 'warning']),
    ),
    PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        completionState: PropTypes.oneOf(['complete', 'incomplete', 'optional', 'warning']).isRequired,
        visible: PropTypes.bool,
      }),
    ),
  ]).isRequired,
  activeSection: PropTypes.string.isRequired,
  onSectionSelect: PropTypes.func.isRequired,
};

/**
 * @example
 * // S812: sections is a plain object from deriveSectionStates() in usePlanDocument.js
 * <PlanDocumentNavigator
 *   sections={{
 *     'overview':              'complete',
 *     'identity':              'warning',
 *     'credit-allocation':     'complete',
 *     'measurement-economics': 'incomplete',
 *     'payout-adjustments':    'optional',
 *     'settlement':            'optional',
 *     'review-governance':     'incomplete',
 *   }}
 *   activeSection="identity"
 *   onSectionSelect={setActiveSection}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 * - Locked eight-section order is encoded directly in presentation configuration.
 * - Structural completion and governance approval are never conflated.
 * - Credit Allocation visibility is governed only by suppliedSection.visible.
 * - No plan status or approval state is accepted as a prop.
 * - Completion state includes readable text alongside every glyph.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 * - none.
 */

/**
 * ACCESSIBILITY NOTES
 * - Navigation uses a native nav/list/button hierarchy.
 * - Active route uses aria-current="page".
 * - Completion states are written in text and additionally exposed through an aria-label.
 * - Icons are aria-hidden because their equivalent text is adjacent.
 */